import { getProductType } from "../../constants/login";
import {
  buildEmailHeaderRow,
  EMAIL_HEADER_CELL_STYLE,
} from "./emailProductLogo";
import { buildEmailNeedHelpSection } from "./emailSupportFooter";

export type CredentialsEmailData = {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  customerName: string;
  onboardingUrl: string;
  productId: number;
  tenantId?: number;
  propertyId?: number;
};

export const credentialsEmailSubject = (customerName: string): string =>
  `Your Agilysys Onboarding login credentials — ${customerName}`;

const credentialRow = (
  label: string,
  value: string,
  valueColor = "#111827",
  isLast = false,
): string => `
  <tr>
    <td style="padding:14px 18px;${isLast ? "" : "border-bottom:1px solid #e5e7eb;"}width:34%;vertical-align:middle;">
      <span style="display:inline-block;font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#6b7280;">${label}</span>
    </td>
    <td style="padding:14px 18px;${isLast ? "" : "border-bottom:1px solid #e5e7eb;"}vertical-align:middle;">
      <span style="font-size:15px;font-weight:600;color:${valueColor};font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace;word-break:break-all;">${value}</span>
    </td>
  </tr>`;

export const buildCredentialsEmailHtml = (
  data: CredentialsEmailData,
): string => {
  const productName = getProductType(data.productId) ?? "Onboarding";
  const emailHeader = buildEmailHeaderRow(data.productId, productName);
  const fullName = [data.firstName, data.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const greeting = fullName ? `Hello ${fullName},` : "Hello,";
  const hasTenant = data.tenantId != null && data.propertyId != null;
  const credentialsBody = [
    credentialRow("Username", data.username, "#111827", false),
    credentialRow("Password", data.password, "#047857", !hasTenant),
    ...(hasTenant
      ? [
          credentialRow(
            "Tenant / Property",
            `${data.tenantId} / ${data.propertyId}`,
            "#111827",
            true,
          ),
        ]
      : []),
  ].join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${credentialsEmailSubject(data.customerName)}</title>
</head>
<body style="margin:0;padding:0;background-color:#eef2f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:linear-gradient(180deg,#ecfdf5 0%,#eef2f7 28%,#eef2f7 100%);padding:40px 16px 48px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:600px;">
          <tr>
            <td style="padding:0 0 14px;text-align:center;">
              <span style="display:inline-block;padding:6px 14px;border-radius:999px;background:#ffffff;border:1px solid #d1fae5;color:#047857;font-size:12px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;box-shadow:0 2px 8px rgba(16,185,129,0.12);">
                Account created
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#ffffff;border-radius:18px;border:1px solid #e5e7eb;overflow:hidden;box-shadow:0 18px 45px rgba(15,23,42,0.08);">
                <tr>
                  <td style="height:6px;background:linear-gradient(90deg,#10b981 0%,#059669 50%,#34d399 100%);font-size:0;line-height:0;">&nbsp;</td>
                </tr>
                <tr>
                  <td style="${EMAIL_HEADER_CELL_STYLE}">
                    ${emailHeader}
                  </td>
                </tr>
                <tr>
                  <td style="padding:34px 36px 10px;">
                    <h1 style="margin:0 0 10px;font-size:28px;line-height:1.2;font-weight:800;color:#0f172a;letter-spacing:-0.03em;">
                      Welcome to Agilysys Onboarding
                    </h1>
                    <p style="margin:0 0 14px;font-size:16px;line-height:1.7;color:#475569;">${greeting}</p>
                    <p style="margin:0;font-size:16px;line-height:1.7;color:#475569;">
                      Your onboarding account is ready. Sign in below to complete your settings template and get your property configured.
                    </p>
                  </td>
                </tr>
                <tr>
                  <td style="padding:18px 36px 8px;">
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-radius:14px;background:linear-gradient(135deg,#f0fdf4 0%,#ecfeff 100%);border:1px solid #bbf7d0;">
                      <tr>
                        <td style="padding:16px 18px;">
                          <p style="margin:0 0 4px;font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#059669;">Customer</p>
                          <p style="margin:0;font-size:18px;font-weight:700;color:#0f172a;">${data.customerName}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:18px 36px 10px;">
                    <p style="margin:0 0 12px;font-size:13px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#64748b;">
                      Your login credentials
                    </p>
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border:1px solid #e2e8f0;border-radius:14px;overflow:hidden;background:#f8fafc;box-shadow:inset 0 1px 0 #ffffff;">
                      ${credentialsBody}
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:10px 36px 8px;">
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-radius:14px;background:#f8fafc;border:1px solid #e2e8f0;">
                      <tr>
                        <td style="padding:18px 20px;">
                          <p style="margin:0 0 12px;font-size:13px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#64748b;">Getting started</p>
                          <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                            <tr>
                              <td style="padding:0 0 10px;font-size:14px;line-height:1.6;color:#475569;">
                                <strong style="color:#0f172a;">1.</strong> Click the button below to open the onboarding portal.
                              </td>
                            </tr>
                            <tr>
                              <td style="padding:0 0 10px;font-size:14px;line-height:1.6;color:#475569;">
                                <strong style="color:#0f172a;">2.</strong> Sign in with the username and password provided above.
                              </td>
                            </tr>
                            <tr>
                              <td style="padding:0;font-size:14px;line-height:1.6;color:#475569;">
                                <strong style="color:#0f172a;">3.</strong> Complete your settings template with your team.
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:22px 36px 34px;" align="center">
                    <a href="${data.onboardingUrl}" style="display:inline-block;min-width:240px;padding:15px 32px;background:linear-gradient(145deg,#10b981 0%,#059669 100%);color:#ffffff;text-decoration:none;font-size:16px;font-weight:700;border-radius:12px;box-shadow:0 10px 24px rgba(16,185,129,0.35);text-align:center;">
                      Sign in to Onboarding &rarr;
                    </a>
                    <p style="margin:18px 0 0;font-size:13px;line-height:1.6;color:#94a3b8;">
                      Or open this link in your browser:<br />
                      <a href="${data.onboardingUrl}" style="color:#059669;text-decoration:none;word-break:break-all;">${data.onboardingUrl}</a>
                    </p>
                  </td>
                </tr>
                <tr>
                  <td style="padding:18px 28px;background:linear-gradient(180deg,#fffbeb 0%,#fef3c7 100%);border-top:1px solid #fde68a;">
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="width:28px;vertical-align:top;font-size:18px;line-height:1;">&#128274;</td>
                        <td style="vertical-align:top;">
                          <p style="margin:0 0 6px;font-size:14px;font-weight:700;color:#92400e;">Keep your credentials secure</p>
                          <p style="margin:0;font-size:13px;line-height:1.6;color:#a16207;">
                            Store this email in a secure location. Do not forward it outside your organization.
                            Change your password after first login if prompted.
                          </p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                ${buildEmailNeedHelpSection()}
                <tr>
                  <td style="padding:22px 36px 28px;background:#f8fafc;border-top:1px solid #e2e8f0;text-align:center;">
                    <p style="margin:0 0 6px;font-size:13px;line-height:1.6;color:#64748b;font-weight:600;">Agilysys Onboarding</p>
                    <p style="margin:0;font-size:12px;line-height:1.6;color:#94a3b8;">
                      This message was sent because an onboarding account was created for you.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};
